import { useCallback, useMemo, useState } from "react";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useDebounce } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import {
  IBidArea,
  ICategoryFilterResponse,
  IFloorResponse,
  IMaterialsResponse,
} from "@miplatform/types";
import {
  filterInactiveAttributes,
  filterMaterialsByAttributes,
  getSelectedAttributeValues,
  sortCategoryAttributes,
} from "@miplatform/utils";
import { useCreateBidContext } from "../CreateBidContext";
import { getMaterialLabel } from "../ReplaceItemsModal/getMaterialLabel";

export function useConfigureBidViewModel() {
  const [openAreaDrawer, setOpenAreaDrawer] = useState(false);
  const [selectedFloor, setSelectedFloor] = useState<IFloorResponse | null>(
    null
  );
  const [addedFloorIds, setAddedFloorIds] = useState<string[]>([]);
  const [previewAreas, setPreviewAreas] = useState<IBidArea[]>([]);
  const [checkedAreas, setCheckedAreas] = useState<IBidArea[]>([]);
  const [showLinkedItems, setShowLinkedItems] = useState(false);
  const [showPriceDetails, setShowPriceDetails] = useState(false);
  const [collapsedFloor, setCollapsedFloor] = useState<Record<string, boolean>>(
    {}
  );

  // Category filter state
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<
    Partial<ICategoryFilterResponse>
  >({});
  const [selectedAttributeValues, setSelectedAttributeValues] = useState<
    Record<string, string[]>
  >({});
  const [search, setSearch] = useState("");

  // Material results state
  const [materialOptions, setMaterialOptions] = useState<IMaterialsResponse[]>(
    []
  );
  const [openMaterialResults, setOpenMaterialResults] = useState(false);
  const [selectedItem, setSelectedItem] = useState<IMaterialsResponse | null>(
    null
  );
  const [isSuppliedByBuilder, setIsSuppliedByBuilder] = useState(false);
  const [planAreaMaterials, setPlanAreaMaterials] = useState<
    IMaterialsResponse[]
  >([]);
  const [linkedMaterialDetails, setLinkedMaterialDetails] = useState<
    IMaterialsResponse[]
  >([]);

  // Replace item modal state
  const [replaceModal, setReplaceModal] = useState<ReplaceModalState>({
    open: false,
    item: null,
    area: null,
  });

  const debouncedSearch = useDebounce(search, 300);

  const {
    form,
    floors,
    categoryFilterOptions,
    areaTypes,
    accountDetails,
    getCustomerMaterials,
  } = useCreateBidContext();

  const { watch, setValue } = form;
  const { planAreas = [], builderId } = watch();
  const { maxFloorId } = accountDetails || {};

  const planAreaMaterialIds = useMemo(() => {
    const ids = planAreas.flatMap((a) => a.materials?.map((m) => m.materialId));
    return [...new Set(ids)].filter(Boolean);
  }, [planAreas]);

  const getPlanAreaMaterial = async () => {
    const response = await getCustomerMaterials({
      accountId: builderId,
      filters: { id: { in: { type: "guid", value: planAreaMaterialIds } } },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setPlanAreaMaterials(response.data);
    }
  };

  const getLinkedMaterials = async (parentMaterials: IMaterialsResponse[]) => {
    const ids = parentMaterials.flatMap((m) =>
      m.linkedMaterials?.map((l) => l.materialId)
    );
    const linkedIds = [...new Set(ids)].filter(Boolean);
    const response = await getCustomerMaterials({
      accountId: builderId,
      filters: { id: { in: { type: "guid", value: linkedIds } } },
    });
    if (response.status === HTTP_STATUSES.OK) {
      setLinkedMaterialDetails(response.data);
    }
  };

  const getMaterialOptions = async () => {
    const filters = {
      or: [
        { category: { itemCode: { contains: debouncedSearch } } },
        { category: { itemDescription: { contains: debouncedSearch } } },
      ],
      ...(selectedCategory?.categoryId && {
        category: {
          categoryId: { type: "guid", value: selectedCategory.categoryId },
        },
      }),
    };

    const response = await getCustomerMaterials({
      accountId: builderId,
      filters,
    });

    if (response.status === HTTP_STATUSES.OK) {
      const filteredData = filterInactiveAttributes(response.data).map(
        (material: IMaterialsResponse) => ({
          ...material,
          materialId: material.id,
        })
      );
      setMaterialOptions(filteredData);
      setOpenMaterialResults(true);
    }
  };

  // Grouped areas combining planAreas and previewAreas
  const groupedAreas = useMemo(() => {
    const combinedAreas = [...planAreas, ...previewAreas];
    const sortedAreas = combinedAreas.sort(
      (a, b) => a.displayOrder - b.displayOrder
    );
    if (!sortedAreas.length) return {};
    return Object.groupBy(sortedAreas, (area) => area.floorId);
  }, [planAreas, previewAreas]);

  const addedFloors = useMemo(() => {
    const existingFloorIds = planAreas?.map((a) => a.floorId);
    const allFloorIds = [...new Set([...addedFloorIds, ...existingFloorIds])];
    return allFloorIds
      .map((id) => floors.find((f) => f.id === id))
      .filter((f) => !!f)
      .sort((a, b) => a.displayOrder - b.displayOrder);
  }, [planAreas, floors, addedFloorIds]);

  const hasMissingCategories = useMemo(() => {
    if (!planAreas?.length || !planAreaMaterials.length) return true;
    return planAreas.some((area) => {
      const areaType = areaTypes.find((at) => at.id === area.areaTypeId);
      if (!areaType) return false;
      const requiredCategories = areaType.categories.filter(
        (c) => c.isRequired
      );
      if (!requiredCategories.length) return false;
      const materialIds = area.materials?.map((m) => m.materialId);
      const areaMaterials = planAreaMaterials.filter((m) =>
        materialIds?.includes(m.id)
      );
      return requiredCategories.some(
        (cat) =>
          !areaMaterials.some((m) => m.category?.categoryId === cat.categoryId)
      );
    });
  }, [planAreas, areaTypes, planAreaMaterials]);

  const isAddFloorDisabled = useMemo(() => {
    if (!maxFloorId) return true;
    const maxFloor = floors.find((f) => f.id === maxFloorId);
    if (!maxFloor) return true;
    const addedIds = new Set(addedFloors.map((f) => f.id));
    const hasMissingFloorUpToMax = floors.some(
      (f) => f.displayOrder <= maxFloor.displayOrder && !addedIds.has(f.id)
    );
    return !hasMissingFloorUpToMax;
  }, [addedFloors, floors, accountDetails]);

  const handleAddFloor = () => {
    const existingFloorIds = planAreas.map((area) => area.floorId);
    const allFloorIds = [...new Set([...addedFloorIds, ...existingFloorIds])];
    const nextFloor = floors.find((f) => !allFloorIds.includes(f.id));
    if (nextFloor) {
      setAddedFloorIds((prev) => [...prev, nextFloor.id]);
    }
  };

  const handleRemoveFloor = (floor: IFloorResponse) => {
    const updatedAreas = planAreas.filter((area) => area.floorId !== floor.id);
    setValue("planAreas", updatedAreas, { shouldDirty: true });
    setAddedFloorIds((prev) => prev.filter((id) => id !== floor.id));
    setPreviewAreas((prev) => prev.filter((a) => a.floorId !== floor.id));
  };

  const handleOpenCategoryFilter = (
    area: IBidArea,
    category?: ICategoryFilterResponse
  ) => {
    setCheckedAreas([area]);
    setShowCategoryFilter(true);
    if (category) {
      const sortedCategory = sortCategoryAttributes(category);
      setSelectedCategory(sortedCategory);
    }
  };

  const handleCloseCategoryFilter = () => {
    setShowCategoryFilter(false);
    setCheckedAreas([]);
    resetMaterialDrawerStates();
  };

  const handleCategoryClick = (category: ICategoryFilterResponse) => {
    const sortedCategory = sortCategoryAttributes(category);
    setSelectedCategory(sortedCategory);
  };

  const handleAttributeValueClick = (
    attributeId: string,
    attributeValue: string
  ) => {
    getSelectedAttributeValues(
      attributeId,
      attributeValue,
      selectedAttributeValues,
      setSelectedAttributeValues
    );
  };

  const resetMaterialDrawerStates = () => {
    setSelectedCategory({});
    setSelectedAttributeValues({});
    setSearch("");
    setMaterialOptions([]);
    setOpenMaterialResults(false);
  };

  const handleCheckArea = (area: IBidArea, checked: boolean) => {
    if (checked) {
      setCheckedAreas((prev) => [...prev, area]);
    } else {
      setCheckedAreas((prev) => prev.filter((a) => a.id !== area.id));
    }
  };

  const handleSelectItem = (material: IMaterialsResponse) => {
    if (selectedItem?.id === material.id) return;
    setIsSuppliedByBuilder(false);
    setSelectedItem(material);
  };

  const handleCancelItem = () => {
    setSelectedItem(null);
    setCheckedAreas([]);
    setIsSuppliedByBuilder(false);
  };

  const handleAddItemToAreas = useCallback(() => {
    if (!selectedItem || !checkedAreas.length) return;

    const updatedAreas = planAreas.map((a) => {
      const isChecked = checkedAreas.some((c) => c.id === a.id);
      if (!isChecked) return a;

      const existingMaterials = a.materials || [];
      const alreadyAdded = existingMaterials.some(
        (m) => m.materialId === (selectedItem.materialId || selectedItem.id)
      );
      if (alreadyAdded) return a;

      return {
        ...a,
        materials: [
          ...existingMaterials,
          {
            materialId: selectedItem.materialId || selectedItem.id,
            suppliedByBuilder: isSuppliedByBuilder,
            quantity: 1,
          },
        ],
      };
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });

    // reset
    setSelectedItem(null);
    setCheckedAreas([]);
    setIsSuppliedByBuilder(false);
    setSelectedCategory({});
    setSelectedAttributeValues({});
  }, [planAreas, selectedItem, checkedAreas, isSuppliedByBuilder]);

  const handleResetEverything = () => {
    setShowCategoryFilter(false);
    setOpenAreaDrawer(false);
    setSelectedFloor(null);
    setPreviewAreas([]);
    setSelectedItem(null);
    setCheckedAreas([]);
    setIsSuppliedByBuilder(false);
    resetMaterialDrawerStates();
  };

  const handleOpenReplaceItemModal = ({
    item,
    area,
  }: {
    item: IMaterialsResponse;
    area: IBidArea;
  }) => {
    setReplaceModal({ open: true, item, area });
  };

  const handleCloseReplaceItemModal = () => {
    setReplaceModal({ open: false, item: null, area: null });
  };

  const handleReplaceItem = (
    item: IMaterialsResponse,
    replacementItem: IMaterialsResponse,
    selectedAreaIds: string[]
  ) => {
    const oldMaterialId = item.materialId!;
    const newMaterialId = replacementItem.materialId!;

    const updatedAreas = planAreas.map((area) => {
      if (!selectedAreaIds.includes(area.id)) return area;

      const existingMaterials = area.materials || [];
      const oldMaterial = existingMaterials.find(
        (m) => m.materialId === oldMaterialId
      );
      if (!oldMaterial) return area;

      const materials = existingMaterials
        .filter(
          (m) =>
            m.materialId !== oldMaterialId && m.materialId !== newMaterialId
        )
        .concat({
          materialId: newMaterialId,
          quantity: oldMaterial.quantity ?? 1,
          suppliedByBuilder: oldMaterial.suppliedByBuilder ?? false,
        });

      return { ...area, materials };
    });

    setValue("planAreas", updatedAreas, { shouldDirty: true });
    toastService.info({
      text: `"${getMaterialLabel(item)}" replaced with "${getMaterialLabel(
        replacementItem
      )}"`,
      duration: 6000,
    });
    handleCloseReplaceItemModal();
  };

  const handleCollapseFloor = (floorId: string) => {
    setCollapsedFloor((prev) => ({
      ...prev,
      [floorId]: !prev[floorId],
    }));
  };

  const getAreaType = (area: IBidArea) =>
    areaTypes.find((at) => at.id === area.areaTypeId);

  const isPreviewArea = (area: IBidArea) =>
    previewAreas.some((p) => p.id === area.id);

  const materialResults = useMemo(() => {
    if (
      selectedCategory?.categoryId &&
      Object.keys(selectedAttributeValues).length > 0
    ) {
      return filterMaterialsByAttributes(
        materialOptions,
        selectedAttributeValues
      );
    }

    return materialOptions;
  }, [materialOptions, selectedCategory?.categoryId, selectedAttributeValues]);

  // prevents refetching on quantity change and same material added
  const planAreaMaterialIdsKey = useMemo(
    () => planAreaMaterialIds.join(","),
    [planAreaMaterialIds]
  );

  return {
    floors,
    openAreaDrawer,
    selectedFloor,
    addedFloorIds,
    addedFloors,
    groupedAreas,
    planAreas,
    replaceModal,
    showCategoryFilter,
    selectedCategory,
    selectedAttributeValues,
    search,
    materialOptions,
    materialResults,
    openMaterialResults,
    selectedItem,
    isSuppliedByBuilder,
    checkedAreas,
    planAreaMaterials,
    planAreaMaterialIdsKey,
    hasMissingCategories,
    planAreaMaterialIds,
    debouncedSearch,
    categoryFilterOptions,
    linkedMaterialDetails,
    showLinkedItems,
    collapsedFloor,
    showPriceDetails,
    builderId,
    maxFloorId,
    isAddFloorDisabled,
    handleCollapseFloor,
    setShowLinkedItems,
    setShowPriceDetails,
    setSearch,
    setOpenAreaDrawer,
    setSelectedFloor,
    setPreviewAreas,
    setOpenMaterialResults,
    setIsSuppliedByBuilder,
    setSelectedItem,
    getAreaType,
    getMaterialOptions,
    getPlanAreaMaterial,
    getLinkedMaterials,
    isPreviewArea,
    handleAddFloor,
    handleRemoveFloor,
    handleOpenCategoryFilter,
    handleCloseCategoryFilter,
    handleCategoryClick,
    handleAttributeValueClick,
    handleCheckArea,
    handleCancelItem,
    handleSelectItem,
    handleAddItemToAreas,
    handleResetEverything,
    handleOpenReplaceItemModal,
    handleCloseReplaceItemModal,
    handleReplaceItem,
    resetMaterialDrawerStates,
  };
}

interface ReplaceModalState {
  open: boolean;
  item: IMaterialsResponse | null;
  area: IBidArea | null;
}
